import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import Button from "../../Components/Button";
import Avatar from "../../Components/Avatar";
import { useAuth } from "../../Context/authContext";
import {
  getMentorReviewIdeas,
  getMentorInvites,
  respondToMentorInvite,
} from "../../Utils/apiServices";
import { extractIdeasData, formatDate } from "./utils";
import toast from "../../Utils/toastManager";

// Presentational card shared by review ideas and mentor invitations so both
// surfaces show the same rich idea preview (image, summary, tags). Invitations
// pass a `ribbon`, an `extra` "invited by" row, and a `footer` with actions.
const ReviewIdeaCard = ({
  idea,
  clickable = false,
  onOpen,
  ribbon = null,
  extra = null,
  footer = null,
  cardClassName = "",
  footerClassName = "",
}) => {
  const interactiveProps = clickable
    ? {
        role: "link",
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen?.();
          }
        },
      }
    : {};

  return (
    <Card
      {...interactiveProps}
      footer={footer}
      footerClassName={footerClassName}
      className={`flex flex-col gap-5 min-w-80 w-full h-auto border-1 ${
        clickable
          ? "cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          : ""
      } ${cardClassName}`}
    >
      <div className="relative w-full h-auto flex flex-col gap-2 items-start justify-start rounded-lg overflow-hidden">
        <img
          src={idea.image}
          alt={idea.title}
          className="w-full h-auto object-cover"
        />
        {ribbon}
      </div>
      <div className="w-full h-auto flex flex-col gap-2 items-start justify-start py-2 px-3">
        <div className="w-full h-auto flex flex-col gap-2 items-start justify-start">
          <div className="w-full h-auto flex flex-row gap-2 items-center justify-start">
            <Icon icon="lucide:dot" className="h-6 w-6 text-brand-700 shrink-0" />
            <Typography as="p" className="text-sm font-semibold text-brand-700">
              Submitted: {idea.submissionDate}
            </Typography>
          </div>
          <div className="w-full h-auto flex flex-row gap-2 items-center justify-start">
            <Typography as="h3" className="text-lg font-semibold text-primary-900">
              {idea.title}
            </Typography>
          </div>
          <Typography as="p" className="text-sm text-tertiary-600 text-start">
            {idea.description}
          </Typography>
        </div>
        <div className="w-fit h-auto flex flex-row gap-2 items-center justify-start flex-wrap">
          {idea.tags.map((tag, index) => (
            <div
              key={index}
              className="w-auto h-auto flex flex-row gap-2 items-center justify-start"
            >
              <div className="w-auto h-auto flex flex-row gap-1 items-center justify-start border border-primary rounded-[6px] px-1 py-[2px] shadow-xs">
                {tag.avatar ? (
                  <img
                    src={tag.avatar}
                    alt={tag.label}
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <Icon icon={tag.icon} className="h-6 w-6 text-brand-500" />
                )}
                <Typography as="p" className="text-sm text-secondary-700">
                  {tag.label}
                </Typography>
              </div>
            </div>
          ))}
        </div>
        {extra}
      </div>
    </Card>
  );
};

const MentorIdeasReview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pending secondary-mentor invitations addressed to this mentor.
  const [invites, setInvites] = useState([]);
  const [respondingInviteId, setRespondingInviteId] = useState(null);

  // Map a single mentor item to UI card data.
  // Both the review-ideas API and the invites API return objects shaped like
  // { idea: {...}, primaryStudent: {...}, ... }, so the same mapper serves both.
  const mapMentorIdeaItemToCard = (item) => {
    // Extract idea from the nested structure
    const idea = item?.idea;
    if (!idea) {
      console.warn("Missing idea object in mentor review data:", item);
      return null;
    }

    const problemStatement = idea.problem_statement || "";
    const truncatedDescription = problemStatement.length > 100
      ? problemStatement.substring(0, 100) + "..."
      : problemStatement;

    // Get primary student info for tag
    const primaryStudent = item.primaryStudent;
    const studentName = primaryStudent
      ? `${primaryStudent.first_name || ""} ${primaryStudent.last_name || ""}`.trim()
      : "";
    const studentAvatar = primaryStudent?.profile_picture_url || null;

    return {
      id: idea.id,
      title: idea.title || "",
      description: truncatedDescription,
      image: idea.idea_image || idea.concept_diagram || idea.idea_video_link || "https://via.placeholder.com/400x200?text=No+Image",
      submissionDate: formatDate(idea.created_at),
      tags: [
        ...(studentName ? [{
          label: studentName,
          avatar: studentAvatar,
          icon: "lucide:user"
        }] : []),
        {
          label: idea.participation_mode === "team" ? "Team" : "Solo",
          icon: idea.participation_mode === "team" ? "lucide:users" : "lucide:user"
        },
      ],
      // Store additional metadata for reference
      preferenceOrder: item.preferenceOrder,
      primaryStudent: item.primaryStudent,
      // Store original API data for reference
      _original: item,
    };
  };

  // Map mentor review ideas data to UI format
  // Mentor API returns: [{ idea: {...}, primaryStudent: {...}, preferenceOrder: 1, ideaUsers: [] }]
  const mapMentorIdeasToUI = (ideasData) =>
    ideasData.map(mapMentorIdeaItemToCard).filter(Boolean); // Remove any null entries

  // Fetch mentor review ideas
  const fetchMentorReviewIdeas = async (mentorId, setLoadingState = true) => {
    if (!mentorId) {
      if (setLoadingState) setLoading(false);
      return;
    }

    try {
      if (setLoadingState) setLoading(true);
      const response = await getMentorReviewIdeas(mentorId);
      const ideasData = extractIdeasData(response);
      const mappedIdeas = mapMentorIdeasToUI(ideasData);
      setIdeas(mappedIdeas);
    } catch (error) {
      console.error("Error fetching mentor review ideas:", error);
      setIdeas([]);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  };

  // Fetch pending secondary-mentor invitations for this mentor
  const fetchMentorInvites = async (mentorId) => {
    if (!mentorId) return;
    try {
      const response = await getMentorInvites(mentorId);
      const data = response?.data?.data ?? response?.data ?? [];
      setInvites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching mentor invitations:", error);
      setInvites([]);
    }
  };

  // Accept or decline a secondary-mentor invitation
  const handleRespondInvite = async (invite, accept) => {
    if (!invite?.id || !user?.id) return;
    setRespondingInviteId(invite.id);
    try {
      const res = await respondToMentorInvite(invite.id, {
        accept,
        mentor_id: user.id,
      });
      if (res?.success || res?.status === 200) {
        setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      } else {
        console.error("Failed to respond to invitation:", res);
        toast.error(res?.error || "Failed to respond to the invitation.");
      }
    } catch (error) {
      console.error("Error responding to invitation:", error);
      toast.error(error?.error || "An error occurred while responding to the invitation.");
    } finally {
      setRespondingInviteId(null);
    }
  };

  // Fetch ideas on component mount
  useEffect(() => {
    if (user?.role === 'mentor') {
      fetchMentorReviewIdeas(user?.id);
      fetchMentorInvites(user?.id);
    }
  }, [user?.id, user?.role]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
        <div className="flex items-center justify-center py-20">
          <Typography as="p" className="text-md text-tertiary-600">
            Loading ideas...
          </Typography>
        </div>
      </div>
    );
  }

  // Show review interface
  return (
    <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
      {/* Header Section */}
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col gap-2 items-start justify-start">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Review Ideas
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            Review assigned ideas, view AI summaries, and accept or decline
            assignments
          </Typography>
        </div>
      </div>

      {/* Mentor Invitations (secondary-mentor co-mentor invites) */}
      {invites.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Icon icon="lucide:mail" className="h-5 w-5 text-violet-600" />
            <Typography as="h2" className="text-lg font-semibold text-primary-900">
              Mentor Invitations
            </Typography>
            <span className="inline-flex items-center justify-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
              {invites.length}
            </span>
          </div>
          <div className="w-full h-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {invites.map((invite) => {
              const card = mapMentorIdeaItemToCard(invite);
              if (!card) return null;
              const inviterName =
                `${invite.inviter?.first_name || ""} ${invite.inviter?.last_name || ""}`.trim() ||
                invite.inviter?.email ||
                "A mentor";
              const isResponding = respondingInviteId === invite.id;
              return (
                <ReviewIdeaCard
                  key={invite.id}
                  idea={card}
                  cardClassName="border-violet-200"
                  footerClassName="bg-violet-50/60 border-violet-200"
                  ribbon={
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-violet-600 px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
                      <Icon icon="lucide:mail" className="h-3.5 w-3.5" />
                      Invitation
                    </span>
                  }
                  extra={
                    <div className="w-full flex flex-row gap-2 items-center justify-start pt-2 mt-1 border-t border-slate-100">
                      <Avatar
                        src={invite.inviter?.profile_picture_url}
                        firstName={invite.inviter?.first_name}
                        lastName={invite.inviter?.last_name}
                        email={invite.inviter?.email}
                        alt={inviterName}
                        size="w-6 h-6"
                      />
                      <Typography as="p" className="text-xs text-tertiary-600 truncate">
                        Invited by{" "}
                        <span className="font-medium text-primary-900">
                          {inviterName}
                        </span>
                      </Typography>
                    </div>
                  }
                  footer={
                    <div className="flex flex-wrap items-center gap-2">
                      {invite.idea?.id && (
                        <Button
                          variant="secondary"
                          size="small"
                          disabled={isResponding}
                          startIcon={<Icon icon="lucide:eye" className="h-4 w-4" />}
                          onClick={() =>
                            navigate(`/engagements/${invite.idea.id}`, {
                              state: {
                                pendingInvite: true,
                                inviteId: invite.id,
                                inviterName,
                                primaryStudent: invite.primaryStudent,
                              },
                            })
                          }
                        >
                          Review
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={isResponding}
                        onClick={() => handleRespondInvite(invite, false)}
                      >
                        Decline
                      </Button>
                      <Button
                        size="small"
                        disabled={isResponding}
                        startIcon={
                          isResponding ? (
                            <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin" />
                          ) : (
                            <Icon icon="lucide:check" className="h-4 w-4" />
                          )
                        }
                        onClick={() => handleRespondInvite(invite, true)}
                      >
                        {isResponding ? "Accepting..." : "Accept"}
                      </Button>
                    </div>
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Ideas Grid */}
      {ideas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Icon icon="lucide:lightbulb" className="w-16 h-16 text-slate-400 mb-4" />
          <Typography as="p" className="text-lg font-semibold text-primary-900 mb-2">
            No ideas to review
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            There are currently no ideas assigned to you for review.
          </Typography>
        </div>
      ) : (
        <div className="w-full h-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {ideas.map((idea) => (
            <ReviewIdeaCard
              key={idea.id}
              idea={idea}
              clickable
              onOpen={() =>
                navigate(`/ideas/${idea.id}`, {
                  state: { primaryStudent: idea.primaryStudent },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MentorIdeasReview;

